import {getPrimaryAsset} from '@sollapay/domain/asset-types';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {ConfirmActionModal} from '@/components';
import {
  useCancelPaymentInstructionMutation,
  useResendPaymentInstructionMutation
} from '@/features/buyers/hooks';
import {formatPurchaseAssetNames} from '@/features/buyers/utils';
import {PurchaseDocumentsSection} from '@/features/purchases';
import {
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';

import {AssetHeader} from './AssetHeader';
import {BuyerMetricsCard} from './BuyerMetricsCard';
import {BuyerPaymentHistorySection} from './BuyerPaymentHistorySection';
import {showPaymentInstructionActionToast} from './PaymentInstructionActionToast';
import {PurchaseBuyersSection} from './PurchaseBuyersSection';

import type {
  BuyerDTO,
  BuyerPurchaseWithDocumentsDTO,
  DocumentListItemRawDTO,
  PaymentInstructionDTO
} from '@sollapay/types';
import type {FC} from 'react';

export interface BuyerAssetContentProps {
  purchase: BuyerPurchaseWithDocumentsDTO;
  identityDocuments: DocumentListItemRawDTO[];
  instructions: PaymentInstructionDTO[];
  trustAccountId: string;
  buyerId: string;
  onRequestPayment?: () => void;
  requestPaymentDisabled?: boolean;
  purchaseBuyers?: BuyerDTO[];
}

export const BuyerAssetContent: FC<BuyerAssetContentProps> = ({
  purchase,
  identityDocuments,
  instructions,
  trustAccountId,
  buyerId,
  onRequestPayment,
  requestPaymentDisabled,
  purchaseBuyers
}) => {
  const {t} = useTranslation('trustAccounts');
  const navigate = useNavigate();
  const {canCollectPayments, canResendPayments} = useTrustAccountPermissions();
  const {
    maxCollectionPercentage: maxPercentage,
    showsCollectionCapHint,
    scopesPaymentHistoryToBuyer
  } = useTrustTypeStrategy();
  const resend = useResendPaymentInstructionMutation(trustAccountId);
  const cancel = useCancelPaymentInstructionMutation(trustAccountId);

  const [cancelTarget, setCancelTarget] = useState<PaymentInstructionDTO | null>(null);

  const buyersById = useMemo(() => {
    const map = new Map<string, BuyerDTO>();
    purchaseBuyers
      ?.filter(b => !scopesPaymentHistoryToBuyer || b.id === buyerId)
      .forEach(b => map.set(b.id, b));
    return map;
  }, [purchaseBuyers, scopesPaymentHistoryToBuyer, buyerId]);

  const handleViewBuyer = useCallback(
    (targetBuyerId: string) => {
      navigate(`/trust-accounts/${trustAccountId}/buyers/${targetBuyerId}`);
    },
    [navigate, trustAccountId]
  );

  const handleResend = useCallback(
    (instruction: PaymentInstructionDTO) => {
      resend.mutate(instruction.id);
    },
    [resend]
  );

  const handleCancel = useCallback((instruction: PaymentInstructionDTO) => {
    setCancelTarget(instruction);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (!cancelTarget) return;
    cancel.mutate(cancelTarget.id, {
      onSuccess: () => {
        setCancelTarget(null);
        showPaymentInstructionActionToast('cancelSuccess');
      },
      onError: () => {
        setCancelTarget(null);
        showPaymentInstructionActionToast('cancelError');
      }
    });
  }, [cancel, cancelTarget]);

  const purchaseInstructions = useMemo(
    () => instructions.filter(i => i.trustBuyerPurchaseId === purchase.id),
    [instructions, purchase.id]
  );

  const paymentHistoryInstructions = useMemo(
    () =>
      purchaseInstructions.filter(i => !scopesPaymentHistoryToBuyer || i.buyerPartyId === buyerId),
    [purchaseInstructions, scopesPaymentHistoryToBuyer, buyerId]
  );

  return (
    <div className="flex flex-col gap-6">
      <AssetHeader asset={getPrimaryAsset(purchase)} />

      <BuyerMetricsCard
        purchase={purchase}
        instructions={purchaseInstructions}
        maxPercentage={maxPercentage}
        showsCollectionCap={showsCollectionCapHint}
      />

      {purchaseBuyers && purchaseBuyers.length > 1 && (
        <PurchaseBuyersSection
          buyers={purchaseBuyers}
          currentBuyerId={buyerId}
          onViewBuyer={handleViewBuyer}
        />
      )}

      <BuyerPaymentHistorySection
        instructions={paymentHistoryInstructions}
        assetLabel={formatPurchaseAssetNames(purchase.assets, t)}
        buyersById={buyersById}
        onRequestPayment={onRequestPayment}
        requestPaymentDisabled={requestPaymentDisabled}
        onResend={canResendPayments ? handleResend : undefined}
        onCancel={canCollectPayments ? handleCancel : undefined}
        onViewBuyer={handleViewBuyer}
      />

      <PurchaseDocumentsSection
        documents={purchase.documents}
        identityDocuments={identityDocuments}
        trustAccountId={trustAccountId}
        buyerId={buyerId}
        purchaseId={purchase.id}
      />

      <ConfirmActionModal
        isOpen={!!cancelTarget}
        icon="credit-card-cancel"
        iconVariant="destructive"
        primaryVariant="destructive"
        title={t('view.buyers.detail.cancelPayment.title')}
        description={t('view.buyers.detail.cancelPayment.description', {
          name: cancelTarget
            ? (buyersById.get(cancelTarget.buyerPartyId ?? '')?.fullName ??
              t('view.buyers.detail.cancelPayment.unknownBuyer'))
            : ''
        })}
        confirmLabel={t('view.buyers.detail.cancelPayment.confirmLabel')}
        isLoading={cancel.isPending}
        onConfirm={handleConfirmCancel}
        onClose={() => setCancelTarget(null)}
      />
    </div>
  );
};
